<div id="head"></div>
<h1 class="sr-only">Color Explorer to Convert {{ hex }}, Preview, WCAG Contrast, Schemes &amp; Color Blindness</h1>

<!-- ===================== Hero: one wide box (color + identity) ===================== -->
<div [attr.id]="TOOLS[ 0 ].key + '-' + TOOLS[ 0 ].products[ 0 ]?.key"
     class="default-section initial-section metallic-section no-buttons"
     data-section="top"
     [style.--hero-bg]="heroBackground">
  <!-- the colour's name on top as the Apps & Plugins page's title, its one line under it (draft 2026-10-12); the
       widget under them keeps its swatch and values -->
  <div class="cp-hero-head" *ngIf="dataReady">
    <p class="tools-title" aria-hidden="true">{{ formattedColorName ? formattedColorName : hex }}</p>
    <p class="tools-subtitle">A {{ hsl.l < 32 ? 'dark' : ( hsl.l > 70 ? 'light' : 'medium' ) }}, {{ temperature }} color at {{ hsl.h }}&deg; hue</p>
  </div>
  <app-color-widget *ngIf="dataReady" [hex]="hex" (colorChange)="onWidgetColor( $event )"></app-color-widget>
</div>

<!-- a chip per section under the hero, one line that slides sideways and stays under the nav (draft 2026-10-12) -->
<nav class="cp-chipbar" appStuck *ngIf="dataReady" aria-label="Color Explorer sections">
  <div class="cp-chipbar-row">
    <a *ngFor="let s of pageSections" class="pl-chip" [class.on]="activeSection === s.key" [attr.href]="'#sec-' + s.key"
       (click)="$event.preventDefault(); scrollToSection( s.key )">
      <span>{{ s.label }}</span>
    </a>
  </div>
</nav>

<!-- ===================== Body: every section as a box (draft 2026-10-12) ===================== -->
<div class="cp-body cp-boxes" *ngIf="dataReady">

  <div class="cp-content">

    <!-- Preview & Contrast -->
    <section class="cp-section" id="sec-preview" data-section="preview">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ formattedColorName ? formattedColorName : hex }}</span>
      <h2 class="cp-h2" [style.border-color]="hex">Preview</h2>
      <p class="cp-section-lead">See the color as text, fill, border and shadows with copy-ready CSS, and check how readable it is by the WCAG 2 ratio and APCA (WCAG 3).</p>
      </header>
      <div class="cp-main">

      <div class="cp-preview-cols">
        <div class="cp-preview-live">
          <div class="cp-prev-row">
            <button type="button" class="cp-prev-stage cp-prev-text" [style.color]="hex"
                    (mouseenter)="hoveredRule = 'text'" (mouseleave)="hoveredRule = ''"
                    (click)="copy( cssByKey[ 'text' ], 'pv-text' )">
              <span class="cp-prev-label">Text sample</span>
              <span class="cp-prev-copied" *ngIf="copiedKey === 'pv-text'">Copied</span>
            </button>
          </div>
          <div class="cp-prev-row">
            <button type="button" class="cp-prev-stage cp-prev-fill" [style.background-color]="hex" [style.color]="previewTextColor"
                    (mouseenter)="hoveredRule = 'background'" (mouseleave)="hoveredRule = ''"
                    (click)="copy( cssByKey[ 'background' ], 'pv-bg' )">
              <span class="cp-prev-label">Background sample</span>
              <span class="cp-prev-copied" *ngIf="copiedKey === 'pv-bg'">Copied</span>
            </button>
          </div>
          <div class="cp-prev-row">
            <button type="button" class="cp-prev-stage cp-prev-border" [style.border-color]="hex"
                    (mouseenter)="hoveredRule = 'border'" (mouseleave)="hoveredRule = ''"
                    (click)="copy( cssByKey[ 'border' ], 'pv-border' )">
              <span class="cp-prev-label">Border sample</span>
              <span class="cp-prev-copied" *ngIf="copiedKey === 'pv-border'">Copied</span>
            </button>
          </div>
          <div class="cp-prev-row">
            <button type="button" class="cp-prev-stage cp-prev-textshadow" [style.text-shadow]="textShadowCss"
                    (mouseenter)="hoveredRule = 'text-shadow'" (mouseleave)="hoveredRule = ''"
                    (click)="copy( cssByKey[ 'text-shadow' ], 'pv-tshadow' )">
              <span class="cp-prev-label">Text shadow</span>
              <span class="cp-prev-copied" *ngIf="copiedKey === 'pv-tshadow'">Copied</span>
            </button>
          </div>
          <div class="cp-prev-row">
            <button type="button" class="cp-prev-stage cp-prev-boxshadow" [style.box-shadow]="boxShadowCss"
                    (mouseenter)="hoveredRule = 'box-shadow'" (mouseleave)="hoveredRule = ''"
                    (click)="copy( cssByKey[ 'box-shadow' ], 'pv-bshadow' )">
              <span class="cp-prev-label">Box shadow</span>
              <span class="cp-prev-copied" *ngIf="copiedKey === 'pv-bshadow'">Copied</span>
            </button>
          </div>
        </div>

        <div class="cp-preview-css">
          <div class="cp-window">
            <div class="cp-window-bar">
              <span class="cp-window-dots" aria-hidden="true"><i></i><i></i><i></i></span>
              <span class="cp-window-file">color.css</span>
            </div>
            <pre class="cp-window-code"
                 [style.--cp-editor-bg]="editorBg || null"
                 [style.--cp-editor-fg]="editorFg || null"
                 [style.--cp-syntax-comment]="synComment || null"
                 [style.--cp-syntax-selector]="synSelector || null"
                 [style.--cp-syntax-property]="synProperty || null"
                 [style.--cp-syntax-value]="synValue || null"
                 [style.--cp-syntax-keyword]="synKeyword || null"
                 [style.--cp-syntax-punctuation]="synPunctuation || null"><code><span
                   class="cp-css-rule" *ngFor="let cr of cssRules"
                   [class.cp-css-active]="hoveredRule === cr.key"
                   [innerHTML]="cr.html"></span></code></pre>
          </div>
        </div>
      </div>

      <!-- WCAG: contrast against common surfaces (WCAG 2 ratio + APCA).
           swatch (text on bg) + surface label/ratio + per-check status rows -->
      <div class="cp-wcag-grid">
        <div class="cp-wcag-row" *ngFor="let row of contrastRows">
          <span class="cp-wcag-swatch" [style.background-color]="row.bgColor"><span [style.color]="row.textColor">Aa</span></span>
          <span class="cp-wcag-text">
            <span class="cp-wcag-label">{{ row.label }}</span>
            <button type="button" class="cp-wcag-ratio" (click)="copy( row.ratio + ':1', row.key )">
              {{ copiedKey === row.key ? 'Copied' : row.ratio + ':1' }}
            </button>
          </span>
          <span class="cp-checks">
            <span class="cp-check" [attr.data-level]="row.normal">Normal text · {{ row.normal }}</span>
            <span class="cp-check" [attr.data-level]="row.large">Large text · {{ row.large }}</span>
            <span class="cp-check" [attr.data-level]="row.apcaLevel === 'Fail' ? 'Fail' : ( row.apcaLevel === 'Body text' ? 'AAA' : 'AA' )">APCA Lc {{ row.apca }} · {{ row.apcaLevel }}</span>
          </span>
        </div>

        <!-- This color shown as text ON any colour you pick (editable). -->
        <div class="cp-wcag-row">
          <span class="cp-wcag-swatch" [style.background-color]="pairHex"><span [style.color]="hex">Aa</span></span>
          <span class="cp-wcag-text">
            <label class="cp-wcag-onfield">
              <span class="cp-wcag-on">On</span>
              <span class="cp-wcag-hash">#</span>
              <input type="text" [ngModel]="pairText" (ngModelChange)="onPairTextChange( $event )"
                     (keydown)="onHexKeydown( $event )" (keydown.enter)="onHexEnter( $event )"
                     (focus)="onHexFocus( $event )"
                     (blur)="pairText = pairHex.substring( 1 ).toLowerCase()"
                     maxlength="6" inputmode="latin" spellcheck="false" autocomplete="off" autocapitalize="off"
                     aria-label="Background color to test on"/>
            </label>
            <button type="button" class="cp-wcag-ratio" (click)="copy( pairRatio + ':1', 'wc-pair' )">
              {{ copiedKey === 'wc-pair' ? 'Copied' : pairRatio + ':1' }}
            </button>
          </span>
          <span class="cp-checks">
            <span class="cp-check" [attr.data-level]="pairNormal">Normal text · {{ pairNormal }}</span>
            <span class="cp-check" [attr.data-level]="pairLarge">Large text · {{ pairLarge }}</span>
            <span class="cp-check" [attr.data-level]="pairApcaLevel === 'Fail' ? 'Fail' : ( pairApcaLevel === 'Body text' ? 'AAA' : 'AA' )">APCA Lc {{ pairApca }} · {{ pairApcaLevel }}</span>
          </span>
        </div>

        <!-- Opposite of the custom pair: the picked colour as text ON this color. -->
        <div class="cp-wcag-row">
          <span class="cp-wcag-swatch" [style.background-color]="hex"><span [style.color]="pairFgHex">Aa</span></span>
          <span class="cp-wcag-text">
            <label class="cp-wcag-onfield">
              <span class="cp-wcag-hash">#</span>
              <input type="text" [ngModel]="pairFgText" (ngModelChange)="onPairFgTextChange( $event )"
                     (keydown)="onHexKeydown( $event )" (keydown.enter)="onHexEnter( $event )"
                     (focus)="onHexFocus( $event )"
                     (blur)="pairFgText = pairFgHex.substring( 1 ).toLowerCase()"
                     maxlength="6" inputmode="latin" spellcheck="false" autocomplete="off" autocapitalize="off"
                     aria-label="Text color to test on this color"/>
              <span class="cp-wcag-on cp-wcag-on-after">on</span>
            </label>
            <button type="button" class="cp-wcag-ratio" (click)="copy( pairFgRatio + ':1', 'wc-pairfg' )">
              {{ copiedKey === 'wc-pairfg' ? 'Copied' : pairFgRatio + ':1' }}
            </button>
          </span>
          <span class="cp-checks">
            <span class="cp-check" [attr.data-level]="pairFgNormal">Normal text · {{ pairFgNormal }}</span>
            <span class="cp-check" [attr.data-level]="pairFgLarge">Large text · {{ pairFgLarge }}</span>
            <span class="cp-check" [attr.data-level]="pairFgApcaLevel === 'Fail' ? 'Fail' : ( pairFgApcaLevel === 'Body text' ? 'AAA' : 'AA' )">APCA Lc {{ pairFgApca }} · {{ pairFgApcaLevel }}</span>
          </span>
        </div>
      </div>
      </div>
    </section>

    <!-- Color channels: RGB, HSL, CMYK -->
    <section class="cp-section" id="sec-channels" data-section="channels">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ formattedColorName ? formattedColorName : hex }}</span>
      <h2 class="cp-h2" [style.border-color]="hex">Color Channels</h2>
      <p class="cp-section-lead">The screen (RGB, HSL, HSV) and print (CMYK) channel mix for this color</p>
      </header>
      <div class="cp-main">

      <div class="cp-channel-groups">
        <div class="cp-channel-group">
          <h3 class="cp-h3" [style.border-color]="hex">RGB</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let ch of rgbChannels">
              <span class="cp-channel-label">{{ ch.label }}</span>
              <span class="cp-channel-track" [style.background-color]="ch.bg">
                <span class="cp-channel-fill" [style.width.%]="ch.pct" [style.background-color]="ch.color"></span>
              </span>
              <span class="cp-channel-val">{{ ch.value }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3" [style.border-color]="hex">CMYK</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let ch of cmykChannels">
              <span class="cp-channel-label">{{ ch.label }}</span>
              <span class="cp-channel-track" [style.background-color]="ch.bg">
                <span class="cp-channel-fill" [style.width.%]="ch.value" [style.background-color]="ch.color"></span>
              </span>
              <span class="cp-channel-val">{{ ch.value }}%</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3" [style.border-color]="hex">HSL</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let ch of hslChannels">
              <span class="cp-channel-label">{{ ch.label }}</span>
              <span class="cp-channel-track" [style.background-color]="ch.bg">
                <span class="cp-channel-fill" [style.width.%]="ch.pct" [style.background-color]="ch.color"></span>
              </span>
              <span class="cp-channel-val">{{ ch.value }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3" [style.border-color]="hex">HSV</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let ch of hsvChannels">
              <span class="cp-channel-label">{{ ch.label }}</span>
              <span class="cp-channel-track" [style.background-color]="ch.bg">
                <span class="cp-channel-fill" [style.width.%]="ch.pct" [style.background-color]="ch.color"></span>
              </span>
              <span class="cp-channel-val">{{ ch.value }}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Color Conversions -->
    <section class="cp-section" id="sec-conversions" data-section="conversions">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ formattedColorName ? formattedColorName : hex }}</span>
      <h2 class="cp-h2" [style.border-color]="hex">Color Conversions</h2>
      <p class="cp-section-lead">{{ formattedColorName ? formattedColorName : hex }} across every common color space</p>
      </header>
      <div class="cp-main">

      <div class="cp-conv-cols">
        <table class="cp-conv-table cp-conv-col" *ngFor="let col of [ conversionColsLeft, conversionColsRight ]">
          <tbody>
          <ng-container *ngFor="let g of col">
            <tr class="cp-conv-grouprow"><td colspan="3">{{ g.title }}</td></tr>
            <tr *ngFor="let row of g.rows">
              <td class="cp-conv-label">{{ row.label }}</td>
              <td class="cp-conv-value" [title]="row.value">
                <a *ngIf="row.link" [routerLink]="row.link">{{ row.value }}</a>
                <span *ngIf="!row.link">{{ row.value }}</span>
              </td>
              <td class="cp-conv-copy">
                <button type="button" class="cp-std-btn cp-std-btn-copy" (click)="copy( row.value, row.key )">
                  <app-icon name="copy" aria-hidden="true"></app-icon>{{ copiedKey === row.key ? 'Copied' : 'Copy' }}
                </button>
              </td>
            </tr>
          </ng-container>
          </tbody>
        </table>
      </div>
      </div>
    </section>

    <!-- Harmony -->
    <section class="cp-section" id="sec-schemes" data-section="schemes">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ formattedColorName ? formattedColorName : hex }}</span>
      <h2 class="cp-h2" [style.border-color]="hex">Harmony</h2>
      <p class="cp-section-lead">Harmonies built from this color on the color wheel</p>
      </header>
      <div class="cp-main">

      <!-- Each scheme is its own full-width row: the colour squares on the left,
           the harmony wheel (same component / style as the blog harmony post) on
           the right, illustrating where the scheme sits on the wheel. -->

      <!-- Analogous: three-segment chip, the inert base in the MIDDLE flanked by its
           two nearest analogous neighbours (left round | base square | right round). -->
      <div class="cp-harmony-wheeled">
        <div class="cp-scheme">
          <h3 class="cp-h3" [style.border-color]="hex">Analogous</h3>
          <p class="cp-scheme-desc">Neighbouring hues. Calm, cohesive, easy on the eyes.</p>
          <div class="cp-cv-item cp-comp-item">
            <div class="cp-cv-swatch-col">
              <span class="cp-cv-chip cp-split-chip">
                <button type="button" class="cp-cv-orig" [style.background-color]="analogousTrio[ 0 ]"
                        (click)="copy( analogousTrio[ 0 ], 'ana-0' )"
                        [attr.aria-label]="'Copy ' + analogousTrio[ 0 ]"></button>
                <button type="button" class="cp-cv-sim cp-split-mid" [style.background-color]="hex"
                        (click)="copy( hex, 'ana-base' )" [attr.aria-label]="'Copy ' + hex"></button>
                <button type="button" class="cp-cv-sim" [style.background-color]="analogousTrio[ 2 ]"
                        (click)="copy( analogousTrio[ 2 ], 'ana-2' )"
                        [attr.aria-label]="'Copy ' + analogousTrio[ 2 ]"></button>
              </span>
              <span class="cp-split-hexes">
                <span class="cp-cv-hex">{{ copiedKey === 'ana-0' ? 'Copied' : analogousTrio[ 0 ] }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'ana-base' ? 'Copied' : hex }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'ana-2' ? 'Copied' : analogousTrio[ 2 ] }}</span>
              </span>
            </div>
          </div>
        </div>
        <app-color-wheel class="cp-harmony-wheel" [harmony]="true" [colors]="analogousTrio" [base]="hex" heading=""></app-color-wheel>
      </div>

      <!-- Monochrome: three-segment chip, the inert base in the MIDDLE between a
           darker and a lighter step of the same hue. -->
      <div class="cp-harmony-wheeled">
        <div class="cp-scheme">
          <h3 class="cp-h3" [style.border-color]="hex">Monochrome</h3>
          <p class="cp-scheme-desc">One hue, varied in lightness. Quiet, clean, simple to balance.</p>
          <div class="cp-cv-item cp-comp-item">
            <div class="cp-cv-swatch-col">
              <span class="cp-cv-chip cp-split-chip">
                <button type="button" class="cp-cv-orig" [style.background-color]="monochromeTrio[ 0 ]"
                        (click)="copy( monochromeTrio[ 0 ], 'mono-0' )"
                        [attr.aria-label]="'Copy ' + monochromeTrio[ 0 ]"></button>
                <button type="button" class="cp-cv-sim cp-split-mid" [style.background-color]="hex"
                        (click)="copy( hex, 'mono-base' )" [attr.aria-label]="'Copy ' + hex"></button>
                <button type="button" class="cp-cv-sim" [style.background-color]="monochromeTrio[ 2 ]"
                        (click)="copy( monochromeTrio[ 2 ], 'mono-2' )"
                        [attr.aria-label]="'Copy ' + monochromeTrio[ 2 ]"></button>
              </span>
              <span class="cp-split-hexes">
                <span class="cp-cv-hex">{{ copiedKey === 'mono-0' ? 'Copied' : monochromeTrio[ 0 ] }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'mono-base' ? 'Copied' : hex }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'mono-2' ? 'Copied' : monochromeTrio[ 2 ] }}</span>
              </span>
            </div>
          </div>
        </div>
        <app-color-wheel class="cp-harmony-wheel" [harmony]="true" [colors]="monochromeTrio" [base]="hex" heading=""></app-color-wheel>
      </div>

      <!-- Complementary: the Color Vision chip layout, an inert left half (this
           color) and a clickable right half (its complement). -->
      <div class="cp-harmony-wheeled">
        <div class="cp-scheme">
          <h3 class="cp-h3" [style.border-color]="hex">Complementary</h3>
          <p class="cp-scheme-desc">The opposite hue. Maximum contrast, one bold accent.</p>
          <div class="cp-cv-item cp-comp-item">
            <div class="cp-cv-swatch-col">
              <span class="cp-cv-chip">
                <button type="button" class="cp-cv-orig" [style.background-color]="hex"
                        (click)="copy( hex, 'comp-base' )" [attr.aria-label]="'Copy ' + hex"></button>
                <button type="button" class="cp-cv-sim" [style.background-color]="complementaryColors[ 1 ]"
                        (click)="copy( complementaryColors[ 1 ], 'comp-1' )"
                        [attr.aria-label]="'Copy ' + complementaryColors[ 1 ]"></button>
              </span>
              <span class="cp-comp-hexes">
                <span class="cp-cv-hex">{{ copiedKey === 'comp-base' ? 'Copied' : hex }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'comp-1' ? 'Copied' : complementaryColors[ 1 ] }}</span>
              </span>
            </div>
          </div>
        </div>
        <app-color-wheel class="cp-harmony-wheel" [harmony]="true" [colors]="complementaryScheme" [base]="hex" heading=""></app-color-wheel>
      </div>

      <!-- Split Complementary: same Color Vision chip style as Complementary,
           but the inert base sits beside its TWO split-complement partners. -->
      <div class="cp-harmony-wheeled">
        <div class="cp-scheme">
          <h3 class="cp-h3" [style.border-color]="hex">Split Complementary</h3>
          <p class="cp-scheme-desc">The two hues beside the opposite. Softer tension, more range.</p>
          <div class="cp-cv-item cp-comp-item">
            <div class="cp-cv-swatch-col">
              <span class="cp-cv-chip cp-split-chip">
                <button type="button" class="cp-cv-orig" [style.background-color]="hex"
                        (click)="copy( hex, 'split-base' )" [attr.aria-label]="'Copy ' + hex"></button>
                <button type="button" class="cp-cv-sim cp-split-mid" [style.background-color]="splitComplementaryColors[ 1 ]"
                        (click)="copy( splitComplementaryColors[ 1 ], 'split-1' )"
                        [attr.aria-label]="'Copy ' + splitComplementaryColors[ 1 ]"></button>
                <button type="button" class="cp-cv-sim" [style.background-color]="splitComplementaryColors[ 2 ]"
                        (click)="copy( splitComplementaryColors[ 2 ], 'split-2' )"
                        [attr.aria-label]="'Copy ' + splitComplementaryColors[ 2 ]"></button>
              </span>
              <span class="cp-split-hexes">
                <span class="cp-cv-hex">{{ copiedKey === 'split-base' ? 'Copied' : hex }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'split-1' ? 'Copied' : splitComplementaryColors[ 1 ] }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'split-2' ? 'Copied' : splitComplementaryColors[ 2 ] }}</span>
              </span>
            </div>
          </div>
        </div>
        <app-color-wheel class="cp-harmony-wheel" [harmony]="true" [colors]="splitScheme" [base]="hex" heading=""></app-color-wheel>
      </div>

      <!-- Triadic: same three-segment chip as Split Complementary, inert base
           beside its two triadic partners. -->
      <div class="cp-harmony-wheeled">
        <div class="cp-scheme">
          <h3 class="cp-h3" [style.border-color]="hex">Triadic</h3>
          <p class="cp-scheme-desc">Three evenly spaced hues. Balanced but lively.</p>
          <div class="cp-cv-item cp-comp-item">
            <div class="cp-cv-swatch-col">
              <span class="cp-cv-chip cp-split-chip">
                <button type="button" class="cp-cv-orig" [style.background-color]="hex"
                        (click)="copy( hex, 'tri-base' )" [attr.aria-label]="'Copy ' + hex"></button>
                <button type="button" class="cp-cv-sim cp-split-mid" [style.background-color]="triadicColors[ 1 ]"
                        (click)="copy( triadicColors[ 1 ], 'tri-1' )"
                        [attr.aria-label]="'Copy ' + triadicColors[ 1 ]"></button>
                <button type="button" class="cp-cv-sim" [style.background-color]="triadicColors[ 2 ]"
                        (click)="copy( triadicColors[ 2 ], 'tri-2' )"
                        [attr.aria-label]="'Copy ' + triadicColors[ 2 ]"></button>
              </span>
              <span class="cp-split-hexes">
                <span class="cp-cv-hex">{{ copiedKey === 'tri-base' ? 'Copied' : hex }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'tri-1' ? 'Copied' : triadicColors[ 1 ] }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'tri-2' ? 'Copied' : triadicColors[ 2 ] }}</span>
              </span>
            </div>
          </div>
        </div>
        <app-color-wheel class="cp-harmony-wheel" [harmony]="true" [colors]="triadicScheme" [base]="hex" shape="polygon" heading=""></app-color-wheel>
      </div>

      <!-- Tetradic: four-segment chip like Triadic, inert base beside its three
           tetradic partners; the wheel joins the four as a rectangle. -->
      <div class="cp-harmony-wheeled">
        <div class="cp-scheme">
          <h3 class="cp-h3" [style.border-color]="hex">Tetradic</h3>
          <p class="cp-scheme-desc">Two complementary pairs. The richest scheme, hardest to balance.</p>
          <div class="cp-cv-item cp-comp-item">
            <div class="cp-cv-swatch-col">
              <span class="cp-cv-chip cp-tetra-chip">
                <button type="button" class="cp-cv-orig" [style.background-color]="hex"
                        (click)="copy( hex, 'tet-base' )" [attr.aria-label]="'Copy ' + hex"></button>
                <button type="button" class="cp-cv-sim cp-split-mid" [style.background-color]="tetradicColors[ 1 ]"
                        (click)="copy( tetradicColors[ 1 ], 'tet-1' )" [attr.aria-label]="'Copy ' + tetradicColors[ 1 ]"></button>
                <button type="button" class="cp-cv-sim cp-split-mid" [style.background-color]="tetradicColors[ 2 ]"
                        (click)="copy( tetradicColors[ 2 ], 'tet-2' )" [attr.aria-label]="'Copy ' + tetradicColors[ 2 ]"></button>
                <button type="button" class="cp-cv-sim" [style.background-color]="tetradicColors[ 3 ]"
                        (click)="copy( tetradicColors[ 3 ], 'tet-3' )" [attr.aria-label]="'Copy ' + tetradicColors[ 3 ]"></button>
              </span>
              <span class="cp-tetra-hexes">
                <span class="cp-cv-hex">{{ copiedKey === 'tet-base' ? 'Copied' : hex }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'tet-1' ? 'Copied' : tetradicColors[ 1 ] }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'tet-2' ? 'Copied' : tetradicColors[ 2 ] }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'tet-3' ? 'Copied' : tetradicColors[ 3 ] }}</span>
              </span>
            </div>
          </div>
        </div>
        <app-color-wheel class="cp-harmony-wheel" [harmony]="true" [colors]="tetradicScheme" [base]="hex" shape="polygon" heading=""></app-color-wheel>
      </div>

      <!-- Temperature: just this color, marked Warm or Cool, with the wheel split by
           one long line into a warm top half and a cool bottom half. -->
      <div class="cp-harmony-wheeled">
        <div class="cp-scheme">
          <h3 class="cp-h3" [style.border-color]="hex">Temperature</h3>
          <p class="cp-scheme-desc">Warm hues against cool ones. Their balance sets the overall mood.</p>
          <div class="cp-cv-item cp-comp-item">
            <div class="cp-cv-swatch-col">
              <span class="cp-cv-chip cp-temp-chip">
                <button type="button" class="cp-cv-orig cp-temp-seg" [style.background-color]="hex"
                        (click)="copy( hex, 'temp-base' )" [attr.aria-label]="'Copy ' + hex"></button>
              </span>
              <span class="cp-temp-hexes">
                <span class="cp-cv-hex">{{ copiedKey === 'temp-base' ? 'Copied' : ( temperature | titlecase ) }}</span>
              </span>
            </div>
          </div>
        </div>
        <app-color-wheel class="cp-harmony-wheel" [harmony]="true" [colors]="[ hex ]" [base]="hex" [divide]="true" heading=""></app-color-wheel>
      </div>
      </div>
    </section>

    <!-- Shades, Tints & Tones -->
    <section class="cp-section" id="sec-shades" data-section="shades">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ formattedColorName ? formattedColorName : hex }}</span>
      <h2 class="cp-h2" [style.border-color]="hex">Tints, Shades &amp; Tones</h2>
      <p class="cp-section-lead">A shade adds black, a tint adds white, a tone adds gray</p>
      </header>
      <div class="cp-main">

      <div class="cp-scheme" *ngFor="let r of [
            { title: 'Tints', colors: tints },
            { title: 'Shades', colors: shades },
            { title: 'Tones', colors: tones }
          ]">
        <h3 class="cp-h3" [style.border-color]="hex">{{ r.title }}</h3>
        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of r.colors; let i = index">
            <button type="button" class="cp-swatch-color" [style.background-color]="color"
                    (click)="copy( color, r.title + '-' + i )" [attr.aria-label]="'Copy ' + color"></button>
            <span class="cp-swatch-hex">{{ copiedKey === r.title + '-' + i ? 'Copied' : color }}</span>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Temperature -->
    <section class="cp-section" id="sec-temperature" data-section="temperature">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ formattedColorName ? formattedColorName : hex }}</span>
      <h2 class="cp-h2" [style.border-color]="hex">Temperature</h2>
      <p class="cp-section-lead">
        On the warm to cool axis this color reads {{ temperature }}. Its warmer and cooler neighbours follow.
      </p>
      </header>
      <div class="cp-main">

      <div class="cp-scheme" *ngFor="let r of [
            { title: 'Warmer', colors: warmerColors },
            { title: 'Cooler', colors: coolerColors }
          ]">
        <h3 class="cp-h3" [style.border-color]="hex">{{ r.title }}</h3>
        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of r.colors; let i = index">
            <button type="button" class="cp-swatch-color" [style.background-color]="color"
                    (click)="copy( color, r.title + '-' + i )" [attr.aria-label]="'Copy ' + color"></button>
            <span class="cp-swatch-hex">{{ copiedKey === r.title + '-' + i ? 'Copied' : color }}</span>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Color Vision Deficiency -->
    <section class="cp-section" id="sec-blindness" data-section="blindness"
             [style.--cp-editor-bg]="editorBg || null"
             *ngIf="colorBlindnessColors?.items.length > 0">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ formattedColorName ? formattedColorName : hex }}</span>
      <h2 class="cp-h2" [style.border-color]="hex">Color Vision Deficiency</h2>
      <p class="cp-section-lead">How this color is perceived under each color vision deficiency</p>
      </header>
      <div class="cp-main">

      <div class="cp-cv-group" *ngFor="let g of colorBlindnessGroups">
        <h3 class="cp-h3" [style.border-color]="hex">{{ g.group }}</h3>
        <div class="cp-cv-list">
          <div class="cp-cv-item" *ngFor="let t of g.types">
            <div class="cp-cv-swatch-col">
              <span class="cp-cv-chip">
                <button type="button" class="cp-cv-orig" [style.background-color]="hex"
                      (click)="copy( hex, 'cvbase-' + t.key )" [attr.aria-label]="'Copy ' + hex"></button>
                <button type="button" class="cp-cv-sim"
                        [style.background-color]="colorBlindnessColors.items[ t.index ].value.toString()"
                        (click)="copy( colorBlindnessColors.items[ t.index ].value.toString(), 'cv-' + t.key )"
                        [attr.aria-label]="'Copy ' + colorBlindnessColors.items[ t.index ].value.toString()"></button>
              </span>
              <span class="cp-comp-hexes">
                <span class="cp-cv-hex">{{ copiedKey === 'cvbase-' + t.key ? 'Copied' : hex }}</span>
                <span class="cp-cv-hex">{{ copiedKey === 'cv-' + t.key ? 'Copied' : colorBlindnessColors.items[ t.index ].value.toString() }}</span>
              </span>
            </div>
            <div class="cp-cv-info">
              <span class="cp-cv-name">{{ t.title }}</span>
              <span class="cp-cv-note">{{ t.note }}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Palette Examples: full palettes generated from this color with each
         scheme (plus warm/cool to match its temperature). Click to open one in
         the palette tool. -->
    <section class="cp-section" id="sec-palettes" data-section="palettes" *ngIf="paletteExamples.length">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ formattedColorName ? formattedColorName : hex }}</span>
      <h2 class="cp-h2" [style.border-color]="hex">Palette Examples</h2>
      <p class="cp-section-lead">Palettes sourced from {{ formattedColorName ? formattedColorName : hex }} with different schemes</p>
      </header>
      <div class="cp-main">

      <div class="cp-scheme" *ngFor="let ex of paletteExamples">
        <div class="cp-palex-head">
          <h3 class="cp-h3" [style.border-color]="hex">{{ ex.label }}<button
                  type="button" class="cp-std-btn cp-palex-open"
                  (click)="openPaletteExample( ex.colors, ex.scheme )"
                  aria-label="Open this palette in the palette tool">
            <app-icon name="arrow-up-right-from-square" aria-hidden="true"></app-icon>
          </button></h3>
        </div>
        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of ex.colors; let i = index">
            <button type="button" class="cp-swatch-color" [style.background-color]="color"
                    (click)="copy( color, 'pex-' + ex.scheme + '-' + i )" [attr.aria-label]="'Copy ' + color"></button>
            <span class="cp-swatch-hex">{{ copiedKey === 'pex-' + ex.scheme + '-' + i ? 'Copied' : color }}</span>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- More Color Tools (owner 2026-10-22): the Color Picker, above Related Themes -->
    <app-more-tools sectionKey="more" title="More Color Tools" lead="Pick any color on your screen and read it the same way, right from your menu bar"
                    [app]="colorPicker" [source]="moreToolsSource"></app-more-tools>

    <!-- Related Themes: same box design as the blog post page (theme artwork +
         scrim + circular theme logo). -->
    <section class="cp-section" id="sec-themes" data-section="themes" *ngIf="relatedThemeList.length">
      <header class="cp-head">
      <h2 class="cp-h2" [style.border-color]="hex">Related Themes</h2>
      <p class="cp-section-lead">IDE themes whose palette sits closest to this color</p>
      </header>
      <div class="cp-main">

      <div class="cp-theme-grid">
        <app-theme-card *ngFor="let t of relatedThemeList"
                        [product]="t.product"
                        [category]="t.category"
                        [compact]="false"
                        [tile]="false"
                        [selectable]="false"
                        [clickable]="true"
                        [screenshotMode]="false">
        </app-theme-card>
      </div>
      </div>
    </section>

    <!-- From The Blog: blog poster cards, same design as the blog page hero. -->
    <section class="cp-section" id="sec-blog" data-section="blog">
      <header class="cp-head">
      <h2 class="cp-h2" [style.border-color]="hex">From The Blog</h2>
      <p class="cp-section-lead">Practical reads on how colors are built and combined</p>
      </header>
      <div class="cp-main">

      <div class="cp-blog-cards">
        <a class="cp-blog-card" *ngFor="let post of blogCards" [routerLink]="post.url"
           [style.--card-accent]="post.coverAccent || null">
          <img class="cp-blog-bg" [src]="post.cover" [alt]="post.title" loading="lazy" decoding="async">
          <span class="cp-blog-scrim"></span>
          <svg class="cp-blog-part" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 210 297" aria-hidden="true">
            <path d="M 31.496131,83.498219 C 17.281586,96.049685 13.920954,122.9803 24.61827,138.63631 v 0 c 8.67477,16.8641 33.95747,26.73174 51.762024,20.2132 v 0 l 54.361716,54.36558 -83.532627,83.53647 60.446297,8e-5 84.02015,-83.53655 -84.82866,-84.83262 C 113.3695,110.57782 103.49808,85.295107 86.634028,76.620298 v 0 C 80.390777,72.357466 72.362085,70.323536 64.161412,70.323536 v 0 c -12.368001,0 -25.118271,4.626039 -32.665281,13.174683"/>
          </svg>
          <span class="cp-blog-content">
            <span class="cp-blog-cat">{{ post.categoryLabel }}</span>
            <span class="cp-blog-title">{{ post.title }}</span>
            <span class="cp-blog-meta">{{ post.dateLabel }} &middot; {{ post.readingTimeMinutes }} min read</span>
          </span>
        </a>
      </div>
      </div>
    </section>


    <!-- How It Works + FAQ closes the page, after the blog: the same closer every tool has -->
    <app-tool-about [lead]="about.lead" [paragraphs]="about.paragraphs" [faq]="about.faq" [accent]="hex" ratingKey="color" ratingName="the Color Explorer"></app-tool-about>

  </div>
</div>
